import {
	OUTCOME_LABEL,
	OUTCOME_NOUN,
	OUTCOME_OTHER,
	type OutcomeKey,
} from './groundingFields';

// The north star the owner confirms on the summary, in the two forms it has to exist in: the
// sentence we show them, and the string the backend parses.
//
// Both are composed HERE and nowhere else. The wire format is pinned by a frozen fixture this
// repo and the backend both check in, so neither side can quietly change the separator.

export interface NorthStar {
	/** An outcome key, or OUTCOME_OTHER when the owner described it in their own words. */
	outcome: OutcomeKey | typeof OUTCOME_OTHER;
	target: number;
	/** Their phrasing, when the outcome is off-vocabulary. Empty otherwise. */
	ownWords?: string;
}

const isOutcomeKey = (v: string): v is OutcomeKey => v in OUTCOME_LABEL;

/** Formats a target the way the owner reads it back: money as money, counts as counts. */
export const formatTarget = (
	outcome: NorthStar['outcome'],
	target: number,
): string =>
	outcome === 'revenue'
		? target.toLocaleString(undefined, {
				style: 'currency',
				currency: 'USD',
				maximumFractionDigits: 0,
			})
		: target.toLocaleString();

/** The sentence shown on the summary row and written into the brief. */
export const formatNorthStar = ({
	outcome,
	target,
	ownWords,
}: NorthStar): string => {
	const amount = formatTarget(outcome, target);
	if (outcome === OUTCOME_OTHER) {
		const words = (ownWords ?? '').trim();
		return words ? `Getting to ${amount} ${words}` : `Getting to ${amount}`;
	}
	return `Getting to ${amount} ${OUTCOME_NOUN[outcome]}`;
};

/** The machine form: `revenue:50000`, or `other:120` for a goal we cannot map. */
export const toNorthStarValue = ({ outcome, target }: NorthStar): string =>
	`${outcome}:${target}`;

// What the owner might type instead of a number when they have not decided yet. Recognised so the
// interview moves on instead of asking a second time and trapping them on the question.
// Two shapes, and the difference between them matters.
//
// A bare token has to stand as the FIRST word, the way it always did. A subject may precede only
// the tokens that cannot open a real answer. That split is the whole rule: "we have no walk-ins
// on Tuesdays" and "we have no shortage of leads" are ANSWERS that happen to start with a subject
// and the word "no", and letting a subject in front of bare "no", "none", "pass", "skip" or
// "later" threw both of them away as declines.
//
// The subject list exists because the pattern used to see only a decline standing as the first
// word, so "I don't have one" — the phrasing the docstring below names — read as an answer we
// failed to parse, and the owner was asked a second time (ADO-1984).
//
// This predicate is reached only when no usable number was found, because `submitNorthStar` reads
// the FIGURE first. So "no less than 500 subscribers" never arrives here, and it is the number
// that protects it rather than this pattern.
const DECLINES =
	/^(?:no|nope|none|skip|pass|later|dunno|unsure|not sure|not really|no idea|no clue|idk|tbd|don'?t know|do not know|don'?t have|do not have|haven'?t decided|have not decided|not decided|not yet|no number|no target|no goal|n\/a|na|(?:i|we)(?:'m|'ve|\s+am|\s+have)?\s+(?:don'?t know|do not know|don'?t have|do not have|haven'?t decided|have not decided|not decided|no idea|no clue|not sure|not really|dunno|unsure|no number|no target|no goal))\b/i;

/** True when the answer is "I don't have one", rather than an answer we failed to read. */
export const declinesNorthStar = (text: string): boolean =>
	DECLINES.test(text.trim());

// Their words for the thing being counted. The vocabulary is the backend's; these are the ways an
// owner writes each one. Longest first, so "sign ups" is not eaten by "up".
//
// EVERY alternative needs a boundary on BOTH sides, not only the abbreviations. `arr` was bare
// first, and this row is tried before `sales`, so the fragment inside an ordinary trade noun
// claimed the answer: "earrings", "arrangements" and "warranty" all read as revenue, which also
// switched the figure to a currency (ADO-1984). `arr\b` alone is not enough either — it still
// takes the surname "Carr".
//
// The whole words carried the same fault and it is not a mislabel. `member` matched inside
// "remember", `orders?` inside "disorders" and `leads?` inside "leading", so "10,000 people who
// remember our name" armed a subscriber target. An own-words goal arms NOTHING on the backend —
// `parse_confirmed_north_star('other:10000')` returns None — while a named measure arms a hard
// judged target. So a fragment does not merely rename the row; it converts an unarmed goal into
// one the strategy is scored against.
//
// A prefix stays a prefix where the inflections need it: `enquir` covers "enquiry" and
// "enquiries", and `member(ship)?s?` covers "memberships". The LEADING boundary is what rejects
// "remember", so widening the tail costs nothing.
const OUTCOME_WORDS: [RegExp, OutcomeKey][] = [
	[
		/\b(?:subscribers?|subscriptions?|subs|member(?:ship)?s?)\b/i,
		'subscribers',
	],
	[/\b(?:sign[\s-]?ups?|registrations?|accounts?)\b/i, 'signups'],
	[/\b(?:followers?|audiences?|fans?)\b/i, 'followers'],
	[/\b(?:bookings?|appointments?|reservations?|sessions?)\b/i, 'bookings'],
	[/\b(?:customers?|clients?|buyers?)\b/i, 'customers'],
	// `enquir` and `inquir` stay PREFIXES, so they keep "enquiry" and "enquiries". Only the
	// leading boundary applies to them; a trailing one would reject every inflection.
	[/\b(?:leads?|prospects?)\b|\b(?:enquir|inquir)/i, 'leads'],
	[/\b(?:revenues?|turnovers?|mrr|arr)\b/i, 'revenue'],
	[/\b(?:sales|orders?|purchases?|units?)\b/i, 'sales'],
];

// A number, optionally with thousands separators or a decimal, optionally suffixed k/m.
//
// Two rules here are load-bearing, and each one was a defect (ADO-1984):
//  - the digits must END in a digit. `[\d,\s]*` used to be free to eat the space after the
//    figure, which left `\s*` matching nothing and let `(k|m)?` take the first letter of the
//    NEXT word. "25 members" read as 25,000,000.
//  - nothing wordlike after the magnitude. "3 monthly bookings" and "2 kits sold" fail it, so the
//    letter stays with its own word. "$50k in revenue" and "about 2.5m turnover" pass it, so a
//    real suffix still scales.
//  - the magnitude is a word as often as a letter. Only `k` and `m` scaled, so "1.5 million
//    subscribers" stored a goal of 2 — the bare digits, rounded, a million times too small.
//
// The test after the magnitude is `(?![-\w])`, NOT `\b`. A hyphen satisfies `\b`, so "3
// million-dollar clients" scaled to 3,000,000 and read back as "Getting to 3,000,000 customers"
// for someone who meant three. A magnitude welded into a compound word is part of that word.
//
// Alternation order does not matter here — the engine backtracks out of `m` into `million` either
// way — so read `(?![-\w])` as the rule doing the work, not the order.
//
// A SPACE joins digits into one number only when a full group of three follows it. The class used
// to be `[\d,\s]*`, which was free to span the space between two independent numbers, and
// `readFigure` strips that space before `Number()`. So a duration standing in front of the thing
// counted was welded onto the goal: "300 90 minute sessions" armed a target of 30,090 (ADO-1984).
// The hyphenated twin "500 3-hour sessions" was always right, because `(?![-\w])` rejects a
// hyphen; nothing played that part for a space.
//
// A COMMA stays unrestricted, and that is deliberate. `formatTarget` calls `toLocaleString` with
// the runtime's own locale, and this parser has to read its own output back, so the grouping is
// not ours to predict: `en-IN` writes 100000 as "1,00,000". Requiring a group of three after a
// comma read that back as 1 — the same goal a hundred thousand times too small. A comma cannot
// divide two numbers the way a space can, so it never needed the rule.
//
// One constant, read twice: `readFigure` reads the number and `parseNorthStarAnswer` strips it
// out of the owner's own words. Two copies would drift, and the stripped copy would then cut a
// different span from the sentence than the one that was read.
const FIGURE =
	/((?:\d[\d,]*)?\d(?:\s\d{3})*(?:\.\d+)?)\s*(thousand|million|k|m)?(?![-\w])/i;

// What each magnitude multiplies by. One map, so the regex above and the arithmetic cannot list
// different sets.
const MAGNITUDE: Record<string, number> = {
	k: 1_000,
	thousand: 1_000,
	m: 1_000_000,
	million: 1_000_000,
};

// The lead-in `formatNorthStar` writes in front of every star. This parser has to read its own
// output back, because the owner is offered the number already on file and can keep it or press
// Send on it unedited. Left in place it landed inside their own words, and the next format put a
// second lead-in in front of it, so the sentence grew by one "Getting" on every pass.
//
// Anchored, so it only ever strips OUR prefix. A "getting" anywhere else is the owner's word and
// stays: "300 people getting certified" counts people getting certified.
const OWN_LEAD_IN = /^getting\s+to\s+/i;

/** A number read out of an answer, and WHERE it was read from. */
interface Figure {
	value: number;
	/** Offset of the first digit in the trimmed answer. The currency test needs it. */
	index: number;
}

/**
 * Read a number out of a typed answer.
 *
 * Handles what people actually write: "500 subscribers", "$50k in revenue", "1,000 sign-ups",
 * "about 2.5k followers". Money words and a leading `$` both mean revenue.
 */
const readFigure = (text: string): Figure | null => {
	const m = FIGURE.exec(text);
	if (!m) return null;
	const digits = Number(m[1].replace(/[,\s]/g, ''));
	if (!Number.isFinite(digits) || digits <= 0) return null;
	const suffix = (m[2] ?? '').toLowerCase();
	return {
		value: Math.round(digits * (MAGNITUDE[suffix] ?? 1)),
		index: m.index,
	};
};

/** True when a currency symbol sits immediately before the figure that was READ. */
const isMoneyFigure = (trimmed: string, figure: Figure): boolean =>
	/[$£€]\s*$/.test(trimmed.slice(0, figure.index));

// The measure NEAREST the figure that was read, not the first row of the table that appears
// anywhere in the sentence. This is the same fault `isMoneyFigure` above was fixed for, in the
// noun list: "100 bookings a month from 5,000 followers" answered `followers:100`, a pair that
// appears nowhere in the sentence, because the followers row is tried before the bookings row
// (ADO-1984).
//
// A measure FOLLOWS the number it counts, in English, so a word after the figure always outranks
// one before it. Plain nearness is not enough: "grow my audience to 10,000 subscribers" puts
// "audience" nearer the figure than "subscribers", and the owner is counting subscribers. A
// measure named before the number is the fallback, which keeps "revenue of 5000" reading as
// revenue. Table order breaks a tie, so every single-measure answer reads exactly as it did.
const outcomeNearestFigure = (
	trimmed: string,
	figure: Figure,
): OutcomeKey | null => {
	let after: { outcome: OutcomeKey; distance: number } | null = null;
	let before: { outcome: OutcomeKey; distance: number } | null = null;
	for (const [pattern, outcome] of OUTCOME_WORDS) {
		for (const match of trimmed.matchAll(
			new RegExp(pattern.source, `${pattern.flags.replace('g', '')}g`),
		)) {
			const start = match.index;
			if (start >= figure.index) {
				const distance = start - figure.index;
				if (after === null || distance < after.distance) {
					after = { outcome, distance };
				}
			} else {
				const distance = figure.index - (start + match[0].length);
				if (before === null || distance < before.distance) {
					before = { outcome, distance };
				}
			}
		}
	}
	return (after ?? before)?.outcome ?? null;
};

// A figure that counts TIME is a date, not a goal: "ask me in 3 months" named a period, and
// reading it as the target stored a number the owner never gave.
//
// Anchored to the figure that was READ, for the reason `isMoneyFigure` above carries. Against the
// whole sentence "500 subscribers in 3 months" matched the trailing period, so the rule could only
// be applied behind a decline — and a sentence that names a period with no hedge in front of it
// stored the period anyway.
//
// The unit still has to follow the figure directly, so a rate said the other way round — "80
// classes a month", "12 weddings a year" — is untouched. Those are answers, and the owner's own
// count is the figure in them.
// The period has to be the thing COUNTED, not a unit in front of the thing counted. "30 day
// passes", "4 quarter-page ads", "52 week course signups" and "6 week-long courses sold" are all
// real countable goals, and reading them as dates refused an answer that already carried a number
// (ADO-1984). Worse, the app refused its own pre-filled sentence: `GroundingFlow` seeds the
// composer with `formatNorthStar`, which writes "Getting to 30 day passes" back.
//
// English writes an attributive modifier in the SINGULAR — "30 day passes", never "30 days
// passes" — so the plural is the mechanical signal that the period is the noun. `(?![-\w])` does
// not separate these cases: a plain space satisfies it, so "30 day passes" stays a date under it.
const READ_FIGURE_IS_PERIOD = /^\d[\d,.\s]*(day|week|month|year|quarter)s\b/i;

// The one date English writes in the singular. "ask me in 1 month" carries no plural to read, so
// the figure itself is what tells the two apart.
const READ_FIGURE_IS_ONE_PERIOD = /^1\s*(day|week|month|year|quarter)\b/i;

/** True when the figure this answer would be READ as is measuring time. */
export const figureNamesAPeriod = (text: string): boolean => {
	const trimmed = text.trim();
	const figure = readFigure(trimmed);
	if (figure === null) return false;
	const fromFigure = trimmed.slice(figure.index);
	return (
		READ_FIGURE_IS_PERIOD.test(fromFigure) ||
		(figure.value === 1 && READ_FIGURE_IS_ONE_PERIOD.test(fromFigure))
	);
};

/**
 * Turn a freely typed answer into the number the strategy is judged on.
 *
 * Returns null when there is no usable number, so the interview can ask again in its own voice
 * rather than refusing the answer with a validation error. An unrecognised THING to count is not
 * a failure: it is stored off-vocabulary with the owner's own words, which is what
 * OUTCOME_OTHER exists for.
 */
export const parseNorthStarAnswer = (text: string): NorthStar | null => {
	const trimmed = text.trim();
	if (!trimmed) return null;
	const figure = readFigure(trimmed);
	if (figure === null) return null;
	const target = figure.value;
	// A currency marker on the figure itself outranks any noun in the sentence. "$300 average
	// customer lifetime value" is money, not 300 customers, and the noun list would have read it
	// as customers because the word is there.
	//
	// The test is tied to the figure that was READ, not to the whole sentence. Against the whole
	// sentence, "500 subscribers paying $10/month" matched the $10 that belongs to a DIFFERENT
	// number and reported 500 dollars of revenue (ADO-1984).
	if (isMoneyFigure(trimmed, figure)) return { outcome: 'revenue', target };
	const outcome = outcomeNearestFigure(trimmed, figure);
	if (outcome) return { outcome, target };
	// They named a number but not one of our eight measures. Keep their phrasing, minus the
	// number itself, so the summary reads back what they said.
	const words = trimmed
		.replace(OWN_LEAD_IN, '')
		.replace(FIGURE, ' ')
		.replace(
			/\b(in|of|to|a|an|the|about|around|roughly|per|month|year|weekly|monthly)\b/gi,
			' ',
		)
		.replace(/[^\p{L}\p{N}\s'-]/gu, ' ')
		.replace(/\s+/g, ' ')
		.trim();
	return { outcome: OUTCOME_OTHER, target, ownWords: words };
};

/**
 * Reads a stored wire value back, for prefilling the editor with what was confirmed before.
 *
 * Mirrors the backend's parse deliberately: an off-vocabulary outcome round-trips as
 * OUTCOME_OTHER rather than being dropped, so reopening the row shows the owner their own number
 * instead of an empty box.
 */
export const parseNorthStarValue = (value: string): NorthStar | null => {
	const at = value.indexOf(':');
	if (at < 0) return null;
	const outcome = value.slice(0, at).trim();
	const target = Number(value.slice(at + 1).trim());
	if (!Number.isFinite(target) || target <= 0) return null;
	if (isOutcomeKey(outcome)) return { outcome, target };
	return outcome === OUTCOME_OTHER ? { outcome: OUTCOME_OTHER, target } : null;
};
